class QuickNav extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.quick-nav__item'));
    this.extraOffset = parseInt(this.style.getPropertyValue('--qn-sticky-offset'), 10) || 0;
    this.isSticky = this.dataset.sticky === 'true';
    this.isPinned = false;
    this.originalTop = null;

    this.refresh();
    this.addEventListener('click', this.handleClick.bind(this));

    this.toggle = this.querySelector('.quick-nav__toggle');
    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.toggleMenu());
    }

    this._onOutsideClick = (event) => {
      if (this.classList.contains('is-open') && !this.contains(event.target)) {
        this.closeMenu();
      }
    };
    document.addEventListener('click', this._onOutsideClick);

    this._onResize = () => {
      // Auto-close the mobile panel if the viewport grows back to desktop width.
      if (window.innerWidth > 749 && this.classList.contains('is-open')) {
        this.closeMenu();
      }
    };
    window.addEventListener('resize', this._onResize);

    // Re-check whenever the theme editor swaps sections in/out, so the
    // preview stays accurate without a full page reload.
    document.addEventListener('shopify:section:load', this.refresh.bind(this));
    document.addEventListener('shopify:section:unload', this.refresh.bind(this));

    // Watch for late-rendering content (e.g. sections that inject their
    // markup asynchronously) so links don't get stuck hidden.
    this._observer = new MutationObserver(() => this.refresh());
    this._observer.observe(document.body, { childList: true, subtree: true });

    if (this.isSticky) {
      this.setupSticky();
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('click', this._onOutsideClick);
  }

  toggleMenu() {
    if (this.classList.contains('is-open')) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.classList.add('is-open');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    this.classList.remove('is-open');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
  }

  refresh() {
    let visibleCount = 0;

    this.items.forEach((item) => {
      const targetId = item.dataset.target;
      const target = targetId ? document.getElementById(targetId) : null;

      if (target) {
        item.hidden = false;
        visibleCount += 1;
      } else {
        item.hidden = true;
      }
    });

    this.hidden = visibleCount === 0;
  }

  // We handle "sticky" ourselves with position: fixed rather than relying on
  // CSS position: sticky, for two reasons:
  // 1. position: sticky silently stops working if any ancestor element has
  //    overflow set to anything other than visible — very common in Shopify
  //    themes — and there's no reliable CSS-only way around that.
  // 2. It lets the bar track the site header's actual current height, so it
  //    tucks in below the header when it slides back into view, and sits at
  //    the very top when the header is hidden.
  setupSticky() {
    // Insert a spacer that we activate (give height) only while the nav is
    // pinned, so pinning it doesn't cause the page content to jump upward.
    this.spacer = document.createElement('div');
    this.spacer.className = 'quick-nav-spacer';
    this.parentNode.insertBefore(this.spacer, this.nextSibling);

    this.header = this.findHeader();

    this._onScroll = () => {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(() => {
        this.updateStickyState();
        this._ticking = false;
      });
    };

    window.addEventListener('scroll', this._onScroll, { passive: true });
    this.updateStickyState();
  }

  findHeader() {
    const configured = (this.dataset.headerSelector || '').trim();
    const candidates = [
      configured,
      'sticky-header',
      '.shopify-section-header-sticky',
      '.header-wrapper',
      '#shopify-section-header',
      'header.header',
      'header',
    ].filter(Boolean);

    for (const selector of candidates) {
      const el = document.querySelector(selector);
      if (el) return el;
    }
    return null;
  }

  updateStickyState() {
    if (this.hidden) return; // nothing to stick if no links are showing

    const headerBottom = this.header
      ? Math.max(0, Math.round(this.header.getBoundingClientRect().bottom))
      : 0;

    if (!this.isPinned) {
      // Keep this measurement fresh while unpinned so it self-corrects if
      // page content above it changes height.
      const rect = this.getBoundingClientRect();
      this.originalTop = rect.top + window.scrollY;
      this.navHeight = rect.height;
    }

    const scrollY = window.scrollY;
    const shouldPin = scrollY + headerBottom >= this.originalTop;

    if (shouldPin && !this.isPinned) {
      this.isPinned = true;
      this.classList.add('is-pinned');
      this.spacer.style.height = `${this.navHeight}px`;
    } else if (!shouldPin && this.isPinned) {
      this.isPinned = false;
      this.classList.remove('is-pinned');
      this.spacer.style.height = '0px';
      this.style.top = ''; // clear leftover pinned offset so relative positioning doesn't shift it
    }

    if (this.isPinned) {
      this.style.top = `${headerBottom + this.extraOffset}px`;
    }
  }

  handleClick(event) {
    const link = event.target.closest('.quick-nav__link');
    if (!link) return;

    this.closeMenu();

    const targetId = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = document.getElementById(targetId);
    if (!target) return;

    event.preventDefault();

    const navHeight = this.getBoundingClientRect().height;
    const headerBottom = this.header
      ? Math.max(0, Math.round(this.header.getBoundingClientRect().bottom))
      : 0;
    const offset = (this.isSticky ? navHeight + headerBottom : 0) + this.extraOffset;

    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({ top: targetPosition, behavior: 'smooth' });

    if (history.pushState) {
      history.pushState(null, '', `#${targetId}`);
    }
  }
}

customElements.define('quick-nav', QuickNav);